// apps/web/src/pages/admin/ResearchReviewPage.tsx
//
// Plan E v1 Foundations Task T13 — admin review queue at
// `/admin/research-review`. Two-pane layout (state-filter list + draft
// editor). Wires to /api/admin/research/* endpoints from PR #560.
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';

interface ResearchPaper {
  id: string;
  arxivId: string;
  title: string;
  draftTitle: string | null;
  draftSummary: string | null;
  draftMd: string | null;
  primaryOntologyTag: string | null;
  state: 'raw' | 'scored' | 'drafted' | 'approved' | 'rejected' | 'published' | 'archived';
  score: number | null;
  scoreDimensions: Record<string, number> | null;
  scoreJustification: string | null;
  authors: string[];
  absUrl: string;
  pdfUrl: string;
  preprintPublishedAt: string;
  rejectReason?: string | null;
}

/**
 * Charter 23 T7 (KAN-150) — response of `GET /admin/research/papers/:id/coi`.
 * Mirrors the server projection; deliberately carries no `evidence` field, which
 * is internal to the register and must never reach a browser.
 */
interface CoiCheck {
  disclosureRequired: boolean;
  disclosure: string | null;
  vendors: Array<{
    slug: string;
    vendorName: string;
    kind: string;
    relationship: string;
  }>;
}

const STATE_FILTERS: ResearchPaper['state'][] = [
  'drafted',
  'approved',
  'rejected',
  'published',
  'scored',
  'archived',
];

export function ResearchReviewPage(): JSX.Element {
  const [stateFilter, setStateFilter] = useState<ResearchPaper['state']>('drafted');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const qc = useQueryClient();

  const listKey = ['research-papers', stateFilter] as const;
  const { data, isLoading } = useQuery<{ papers: ResearchPaper[] }>({
    queryKey: listKey,
    queryFn: async () => {
      const r = await fetch(`/api/admin/research/papers?state=${stateFilter}&limit=50`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });

  const selected = data?.papers.find((p) => p.id === selectedId) ?? null;

  return (
    <div className="grid grid-cols-12 gap-4 h-[calc(100vh-4rem)] p-4">
      <aside className="col-span-4 border-r pr-4 overflow-y-auto">
        <h1 className="text-lg font-semibold mb-3">Research review</h1>
        <div className="flex flex-wrap gap-1 mb-3">
          {STATE_FILTERS.map((s) => (
            <Button
              key={s}
              variant={stateFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setStateFilter(s);
                setSelectedId(null);
              }}
            >
              {s}
            </Button>
          ))}
        </div>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (data?.papers ?? []).length === 0 ? (
          <div className="text-sm text-muted-foreground">No papers in this state.</div>
        ) : (
          <ul className="space-y-1">
            {(data?.papers ?? []).map((p) => (
              <li
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className={`p-2 cursor-pointer rounded text-sm ${
                  selectedId === p.id ? 'bg-primary/10' : 'hover:bg-muted'
                }`}
              >
                <div className="font-medium line-clamp-2">{p.draftTitle ?? p.title}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {p.score !== null ? `score ${p.score}` : '—'} · {p.arxivId}
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>
      <main className="col-span-8 overflow-y-auto">
        {selected ? (
          <ResearchDraftEditor
            paper={selected}
            onMutated={() => qc.invalidateQueries({ queryKey: listKey })}
          />
        ) : (
          <div className="text-muted-foreground text-sm p-4">Select a paper from the queue.</div>
        )}
      </main>
    </div>
  );
}

interface EditorProps {
  paper: ResearchPaper;
  onMutated: () => void;
}

function ResearchDraftEditor({ paper, onMutated }: EditorProps): JSX.Element {
  const [title, setTitle] = useState(paper.draftTitle ?? '');
  const [summary, setSummary] = useState(paper.draftSummary ?? '');
  const [md, setMd] = useState(paper.draftMd ?? '');
  const [rejectReason, setRejectReason] = useState('');
  const [coiAcknowledged, setCoiAcknowledged] = useState(false);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/admin/research/papers/${paper.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftTitle: title,
          draftSummary: summary,
          draftMd: md,
        }),
      });
      if (!r.ok) throw new Error(`PATCH HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: onMutated,
  });

  // Charter 23 T7 (KAN-150) — editorial conflict-of-interest check.
  //
  // Re-fetched whenever the draft changes (the query key includes the copy) so
  // the editor sees a disclosure appear the moment they type a registered
  // vendor's name, not only when they try to publish. The server runs the same
  // evaluation at publish time and refuses with 409 if this was not
  // acknowledged, so this panel is the humane path to a gate that exists
  // regardless of the UI.
  const coiQuery = useQuery<CoiCheck>({
    queryKey: ['research-coi', paper.id, title, summary, md] as const,
    queryFn: async () => {
      const r = await fetch(`/api/admin/research/papers/${paper.id}/coi`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    enabled: paper.state === 'drafted',
  });

  // Acknowledgement is per-draft-revision on purpose: editing the copy after
  // acknowledging resets it, so nobody can tick the box against one version of
  // a brief and publish another.
  useEffect(() => {
    setCoiAcknowledged(false);
  }, [title, summary, md]);

  const coiRequired = coiQuery.data?.disclosureRequired === true;
  const approveBlockedByCoi = coiRequired && !coiAcknowledged;

  const approveMutation = useMutation({
    mutationFn: async () => {
      // Save edits first if any changed
      if (
        title !== paper.draftTitle ||
        summary !== paper.draftSummary ||
        md !== paper.draftMd
      ) {
        await saveMutation.mutateAsync();
      }
      const r = await fetch(`/api/admin/research/papers/${paper.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coiDisclosureAcknowledged: coiAcknowledged }),
      });
      if (r.status === 409) {
        const detail = (await r.json()) as { disclosure?: string };
        throw new Error(
          detail.disclosure
            ? `Publish blocked — conflict-of-interest disclosure not acknowledged. ${detail.disclosure}`
            : 'Publish blocked — conflict-of-interest disclosure not acknowledged.',
        );
      }
      if (!r.ok) throw new Error(`POST approve HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: onMutated,
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/admin/research/papers/${paper.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason }),
      });
      if (!r.ok) throw new Error(`POST reject HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: onMutated,
  });

  const unapproveMutation = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/admin/research/papers/${paper.id}/approve`, {
        method: 'DELETE',
      });
      if (!r.ok) throw new Error(`DELETE approve HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: onMutated,
  });

  return (
    <div className="p-4 space-y-4">
      <header className="text-xs text-muted-foreground space-y-1">
        <div>
          arXiv:{' '}
          <a
            className="underline"
            href={paper.absUrl}
            target="_blank"
            rel="noreferrer"
          >
            {paper.arxivId}
          </a>{' '}
          · State: <strong>{paper.state}</strong>{' '}
          {paper.primaryOntologyTag ? `· tag: ${paper.primaryOntologyTag}` : ''}
        </div>
        {paper.score !== null && (
          <div>
            Score: <strong>{paper.score}</strong>
            {paper.scoreJustification ? ` — ${paper.scoreJustification}` : ''}
          </div>
        )}
        <div>Authors: {paper.authors.join(', ') || '—'}</div>
      </header>
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="text-lg font-medium"
        placeholder="CISO-friendly title"
        disabled={paper.state !== 'drafted'}
      />
      <Textarea
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        rows={2}
        placeholder="TL;DR (1-2 sentences)"
        disabled={paper.state !== 'drafted'}
      />
      <Textarea
        value={md}
        onChange={(e) => setMd(e.target.value)}
        rows={20}
        placeholder="Markdown body"
        disabled={paper.state !== 'drafted'}
      />
      {/* Charter 23 T7 (KAN-150) — mandatory conflict-of-interest step. Renders
          only when the draft names a vendor in the CyberFind commercial-
          relationship register, which is the uncommon case. */}
      {paper.state === 'drafted' && coiRequired && coiQuery.data && (
        <section
          data-testid="research-coi-gate"
          aria-labelledby="research-coi-gate-heading"
          className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4 space-y-3"
        >
          <h2
            id="research-coi-gate-heading"
            className="text-sm font-semibold text-foreground"
          >
            Conflict-of-interest disclosure required
          </h2>
          <p className="text-sm text-muted-foreground">
            This brief names{' '}
            {coiQuery.data.vendors.length === 1 ? 'a vendor' : 'vendors'} CyberFind
            has a commercial relationship with. The disclosure below will be
            published at the top of the story and with the community thread. You
            cannot publish until you have read and acknowledged it.
          </p>
          <p className="rounded border border-border bg-background p-3 text-sm leading-6 text-foreground">
            {coiQuery.data.disclosure}
          </p>
          <label className="flex items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              className="mt-1"
              checked={coiAcknowledged}
              onChange={(e) => setCoiAcknowledged(e.target.checked)}
            />
            <span>
              I have reviewed this disclosure and confirm it is accurate for this
              brief.
            </span>
          </label>
        </section>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        {paper.state === 'drafted' && (
          <>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              Save draft
            </Button>
            {/* No `title` tooltip on the disabled state: disabled buttons do
                not reliably surface tooltips or take focus, so the reason lives
                in the visible gate panel directly above instead. */}
            <Button
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isPending || approveBlockedByCoi}
            >
              Approve + publish
            </Button>
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reject reason (≥10 chars)"
              className="max-w-xs"
            />
            <Button
              variant="destructive"
              onClick={() => rejectMutation.mutate()}
              disabled={rejectReason.length < 10 || rejectMutation.isPending}
            >
              Reject
            </Button>
          </>
        )}
        {(paper.state === 'approved' || paper.state === 'rejected') && (
          <Button
            variant="outline"
            onClick={() => unapproveMutation.mutate()}
            disabled={unapproveMutation.isPending}
          >
            Move back to drafted
          </Button>
        )}
        {paper.state === 'published' && (
          <div className="text-sm text-muted-foreground">
            Already published — cannot reverse from the UI. Edit Discourse + DB
            directly per the runbook.
          </div>
        )}
      </div>
      {paper.rejectReason && (
        <div className="text-sm text-muted-foreground">
          Reject reason: {paper.rejectReason}
        </div>
      )}
    </div>
  );
}
